import { deriveField } from "./fieldGeometry";

// Only active wiring matters. Display names, equipment metadata and object
// property order must not make an otherwise unchanged layout obsolete.
export function physicalLayoutDesignKey(systemDesign) {
  const strings = Object.entries(systemDesign || {}).sort(([a], [b]) => a.localeCompare(b))
    .flatMap(([inverter, mppts]) => (mppts || []).flatMap((mppt, m) =>
      (mppt.inputs || []).flatMap((input, i) => {
        const count = Number(input.modules_per_string ?? input.modulesPerString);
        return count > 0 ? [[inverter, Number(mppt.index ?? m + 1),
          Number(input.index ?? i + 1), mppt.orientation || null, count]] : [];
      })));
  return strings.length ? JSON.stringify(strings) : null;
}

/** Grid slots the plan holds no module in, kept only where the grid still has them. */
export function excludedSlotsOf(spacing) {
  const rows = Array.isArray(spacing?.row_modules) ? spacing.row_modules : [];
  const tiers = Number(spacing?.row_vertical_modules ?? spacing?.rowVerticalModules) || 1;
  const out = [];
  for (const key of spacing?.excluded_slots || []) {
    const [row, column, tier] = String(key).split(":").map(Number);
    if (!Number.isInteger(row) || !Number.isInteger(column) || !Number.isInteger(tier)) continue;
    if (row < 1 || row > rows.length || column < 0 || column >= (Number(rows[row - 1]) || 0) || tier < 0 || tier >= tiers) continue;
    if (!out.includes(key)) out.push(key);
  }
  return out;
}

export function physicalLayoutCount(spacing) {
  if (!Array.isArray(spacing?.row_modules) || !spacing.row_modules.length) return null;
  // The modules the plan actually holds: the grid, less the slots emptied by
  // hand around obstacles. Those slots stay in the grid so the neighbours keep
  // their coordinates; they simply do not count.
  return spacing.row_modules.reduce((sum, count) => sum + (Number(count) || 0), 0)
    * (Number(spacing.row_vertical_modules ?? spacing.rowVerticalModules) || 1)
    - excludedSlotsOf(spacing).length;
}

export function layoutDesignChanged(spacing, totalModules, designKey) {
  const count = physicalLayoutCount(spacing);
  return (count != null && totalModules != null && count !== Number(totalModules)) ||
    Boolean(spacing?.electrical_design_key && designKey && spacing.electrical_design_key !== designKey);
}

/** Rebuild PV slots, keeping the site, array origin and plan axis. */
export function updateLayoutFromDesign(form, { totalModules, moduleWidth, moduleLength,
  portrait, designKey, moduleArray = form.module_array }) {
  const count = Math.max(0, Math.round(Number(totalModules) || 0));
  const oldTiers = Math.max(1, Number(form.row_vertical_modules) || 1);
  // A rectangular tier must never round 11 modules up to 12 (or down to 10).
  const tiers = count % oldTiers === 0 ? oldTiers : 1;
  const pitch = (Number(form.minimum_distance_m) || 1) * tiers / oldTiers;
  const field = deriveField({ totalModules: count, pitch,
    moduleWidth: Number(portrait === false ? moduleLength : moduleWidth) || 1,
    verticalModules: tiers, fieldRows: tiers === oldTiers ? Number(form.field_rows) || null : null });
  const rows = field?.rowModules || [];
  const changedGrid = tiers !== oldTiers || JSON.stringify(rows) !== JSON.stringify(form.row_modules);
  return { ...form, row_vertical_modules: String(tiers), minimum_distance_m: String(pitch),
    field_rows: rows.length ? String(rows.length) : "", row_modules: rows,
    row_labels: null, parcel_tables: null, fit_to_design: true,
    excluded_slots: [], module_assignments: null, manual_layout_edited: false,
    string_overrides: null, electrical_design_key: designKey,
    // Slot residuals cannot be replayed after their row structure changes.
    // Preserve the group transform while rebuilding those slots.
    module_offsets: changedGrid ? {} : form.module_offsets,
    module_array: changedGrid && moduleArray
      ? { ...moduleArray, ...(Number.isFinite(moduleArray.rowSpacingM)
          ? { rowSpacingM: moduleArray.rowSpacingM * tiers / oldTiers } : {}), individualOffsets: {} } : moduleArray,
  };
}
